"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  type Option,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { TABLE, TD, TH, WIDGET_H2 } from "@/components/tool-kit/styles";
import { ExampleButton } from "@/components/tools/publishing-statements/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  checkHighlights,
  countStatus,
  HIGHLIGHT_PRESETS,
  type HighlightCheck,
  type HighlightPresetId,
  type HighlightRules,
  parseHighlights,
} from "@/lib/tool-kit/publishing/highlights";

/** English strings. A translated content file overrides these through `ui`. */
export const RESEARCH_HIGHLIGHTS_CHECKER_STRINGS = {
  inputLabel: "Your highlights — one per line",
  inputPlaceholder:
    "A daily walk improved sleep in adults aged 65 to 80\nWalkers fell asleep 15 minutes sooner than non-walkers",
  inputHint:
    "Bullets, dashes and numbers at the start of a line are removed before counting.",
  presetLabel: "Journal rules",
  presetElsevier: "Elsevier — 3 to 5 highlights, up to 85 characters each",
  presetCell: "Cell Press — 3 to 4 highlights, up to 85 characters each",
  presetCustom: "Custom",
  minLabel: "Minimum highlights",
  maxLabel: "Maximum highlights",
  charsLabel: "Characters per highlight, including spaces",
  tryExample: "Try an example",
  example:
    "A daily 30-minute walk improved sleep in adults aged 65 to 80\nWalkers fell asleep 15 minutes sooner and slept 30 minutes longer\nNo walking-related injuries occurred during the 12-week RCT\nBrisk walking may offer older adults a safer alternative to sleeping pills (Lee, 2021)",
  results: "Check",
  statCount: "Highlights",
  statLongest: "Longest, in characters",
  statOver: "Over the limit",
  countFew:
    "one: {count} highlight — these rules ask for at least {min}.|other: {count} highlights — these rules ask for at least {min}.",
  countMany:
    "one: {count} highlight — these rules allow at most {max}.|other: {count} highlights — these rules allow at most {max}.",
  countOk:
    "one: {count} highlight — within the {min} to {max} these rules ask for.|other: {count} highlights — within the {min} to {max} these rules ask for.",
  colNumber: "#",
  colHighlight: "Highlight",
  colCharacters: "Characters",
  colRemaining: "Left",
  colNotes: "Notes",
  noteOver:
    "one: {count} character over the limit|other: {count} characters over the limit",
  noteCitation: "Remove the citation: {list}",
  noteAcronym: "Spell out or remove: {list}",
  noteAcronymDefined: "Spelled out, but costs characters: {list}",
  noteDuplicate: "Repeats highlight {number}",
  noteFine: "No problems found",
  copy: "Copy highlights",
  copied: "Copied!",
  bullet: "• ",
  prompt: "Paste or type your highlights, one per line.",
  listSeparator: ", ",
} as const;

type Strings = typeof RESEARCH_HIGHLIGHTS_CHECKER_STRINGS;
type NumberFormat = (value: number) => string;

function notesFor(
  check: HighlightCheck,
  index: number,
  checks: readonly HighlightCheck[],
  strings: Strings,
  context: { locale: string; fmt: NumberFormat }
): string[] {
  const notes: string[] = [];
  if (check.remaining < 0) {
    notes.push(
      tPlural(strings.noteOver, context.locale, -check.remaining, {
        count: context.fmt(-check.remaining),
      }).trim()
    );
  }
  if (check.citations.length > 0) {
    notes.push(
      t(strings.noteCitation, {
        list: check.citations.join(strings.listSeparator),
      })
    );
  }
  const undefinedAcronyms = check.acronyms
    .filter((use) => !use.defined)
    .map((use) => use.acronym);
  if (undefinedAcronyms.length > 0) {
    notes.push(
      t(strings.noteAcronym, {
        list: undefinedAcronyms.join(strings.listSeparator),
      })
    );
  }
  const defined = check.acronyms
    .filter((use) => use.defined)
    .map((use) => use.acronym);
  if (defined.length > 0) {
    notes.push(
      t(strings.noteAcronymDefined, {
        list: defined.join(strings.listSeparator),
      })
    );
  }
  if (check.duplicate) {
    const key = check.text.toLocaleLowerCase(context.locale);
    const first = checks.findIndex(
      (other, otherIndex) =>
        otherIndex < index &&
        other.text.toLocaleLowerCase(context.locale) === key
    );
    notes.push(t(strings.noteDuplicate, { number: context.fmt(first + 1) }));
  }
  return notes;
}

function countMessage(
  count: number,
  rules: HighlightRules,
  strings: Strings,
  context: { locale: string; fmt: NumberFormat }
): string {
  const template = {
    few: strings.countFew,
    ok: strings.countOk,
    many: strings.countMany,
  }[countStatus(count, rules)];
  return tPlural(template, context.locale, count, {
    count: context.fmt(count),
    min: context.fmt(rules.minBullets),
    max: context.fmt(rules.maxBullets),
  }).trim();
}

export default function ResearchHighlightsCheckerWidget() {
  const strings = useToolUi(RESEARCH_HIGHLIGHTS_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [preset, setPreset] = useState<HighlightPresetId>("elsevier");
  const [customMin, setCustomMin] = useState("3");
  const [customMax, setCustomMax] = useState("5");
  const [customChars, setCustomChars] = useState("85");

  const rules: HighlightRules =
    preset === "custom"
      ? {
          minBullets: Math.max(parseIntegerInput(customMin) ?? 1, 1),
          maxBullets: Math.max(parseIntegerInput(customMax) ?? 1, 1),
          maxCharacters: Math.max(parseIntegerInput(customChars) ?? 1, 1),
        }
      : HIGHLIGHT_PRESETS[preset];
  const highlights = parseHighlights(text);
  const checks = checkHighlights(highlights, rules, locale);
  const context = { locale, fmt: (value: number) => fmt(value) };
  const longest = Math.max(0, ...checks.map((check) => check.characters));
  const over = checks.filter((check) => check.remaining < 0).length;
  const copyText = highlights
    .map((highlight) => `${strings.bullet}${highlight}`)
    .join("\n");

  const presetOptions: Option<HighlightPresetId>[] = [
    { value: "elsevier", label: strings.presetElsevier },
    { value: "cell", label: strings.presetCell },
    { value: "custom", label: strings.presetCustom },
  ];

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="rhc-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField<HighlightPresetId>
          id="rhc-preset"
          label={strings.presetLabel}
          onChange={setPreset}
          options={presetOptions}
          value={preset}
        />
      </div>
      {preset === "custom" ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="rhc-min"
            label={strings.minLabel}
            numeric
            onChange={setCustomMin}
            value={customMin}
          />
          <TextField
            id="rhc-max"
            label={strings.maxLabel}
            numeric
            onChange={setCustomMax}
            value={customMax}
          />
          <TextField
            id="rhc-chars"
            label={strings.charsLabel}
            numeric
            onChange={setCustomChars}
            value={customChars}
          />
        </div>
      ) : null}
      <ExampleButton
        label={strings.tryExample}
        onClick={() => setText(strings.example)}
      />

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {checks.length === 0 ? (
          <Notice>{strings.prompt}</Notice>
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.statCount}
                value={fmt(checks.length)}
              />
              <StatCard label={strings.statLongest} value={fmt(longest)} />
              <StatCard label={strings.statOver} value={fmt(over)} />
            </StatGrid>
            <Notice>
              {countMessage(checks.length, rules, strings, context)}
            </Notice>
            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className={TABLE}>
                <thead>
                  <tr>
                    <th className={TH} scope="col">
                      {strings.colNumber}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colHighlight}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colCharacters}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colRemaining}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colNotes}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((check, index) => {
                    const notes = notesFor(
                      check,
                      index,
                      checks,
                      strings,
                      context
                    );
                    return (
                      // Highlights may repeat, so position is the only stable key.
                      // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
                      <tr key={index}>
                        <td className={TD}>{fmt(index + 1)}</td>
                        <td className={`${TD} min-w-[16rem]`}>{check.text}</td>
                        <td className={TD}>{fmt(check.characters)}</td>
                        <td
                          className={
                            check.remaining < 0
                              ? `${TD} font-semibold text-destructive`
                              : TD
                          }
                        >
                          {fmt(check.remaining)}
                        </td>
                        <td className={`${TD} text-sm`}>
                          {notes.length > 0 ? (
                            <ul className="list-disc space-y-1 pl-4">
                              {notes.map((note) => (
                                <li key={note}>{note}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-muted-foreground">
                              {strings.noteFine}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copy}
              text={copyText}
            />
          </>
        )}
      </section>
    </div>
  );
}
